import { Component, computed, effect, ElementRef, inject, OnDestroy, signal, untracked } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Transition } from 'angular-ui-router';

import { $state } from 'app/ajs-upgraded-providers';

import { ToolbarEventsService } from 'src/app/core';
import { ConfirmDialogService } from 'src/app/dialogs';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { CardComponent } from 'src/app/shared/components/master-detail-layout/card/card.component';
import { MainBaseComponent } from 'src/app/shared/components/master-detail-layout/main-base/main-base.component';
import { MasterDetailLayoutComponent } from 'src/app/shared/components/master-detail-layout/master-detail-layout.component';
import { subscribeInComponent } from 'src/app/shared/angular-utils';
import { mobileCheck } from 'src/utilities';

import type { SettingDefinition, SettingsActionId, SettingsDeviceSettingId, SettingsLookupId, SettingValue } from '../../models/settings-hub.model';
import { SETTINGS_REGISTRY } from '../../registry/settings-registry';
import { SettingsHubActionsService } from '../../services/settings-hub-actions.service';
import { SettingsHubPostSaveService } from '../../services/settings-hub-post-save.service';
import { SettingsHubStateService } from '../../services/settings-hub-state.service';

import {
    buildSearchCards,
    buildSearchIndex,
    buildSubcategoryCards,
    findSettingLocation,
    flattenSettings,
    getPreferenceKeysBySubcategory,
    getSettingPath,
    getVisibleCategories,
    isSearchable,
    MIN_SEARCH_LENGTH,
    isPreferenceSetting,
    searchSections,
    searchSettings,
    SettingsSearchEntry,
    SettingsViewCard
} from '../../utils/settings-hub.utils';

import { SettingsFieldComponent } from '../settings-field/settings-field.component';
import { SettingsSidebarComponent } from '../settings-sidebar/settings-sidebar.component';

const HUB_STATE = 'app.new.settings.hub';
const HIGHLIGHT_DURATION = 2000;

/**
 * The settings page: a sidebar with the categories of the registry and, for the selected subcategory (or the results of
 * the search of the toolbar), the cards with their settings. Everything shown comes from `SETTINGS_REGISTRY`: the page
 * has no knowledge of any specific setting.
 *
 * The url (`section`, `setting`) always reflects the page, so that a setting can be reached with a link.
 */
@Component({
    selector: 'app-settings-hub',
    standalone: true,
    imports: [
        ActionBtnComponent,
        CardComponent,
        MainBaseComponent,
        MasterDetailLayoutComponent,
        MatButtonModule,
        MatDividerModule,
        MatIconModule,
        NgTemplateOutlet,
        SettingsFieldComponent,
        SettingsSidebarComponent,
        TranslateModule
    ],
    providers: [SettingsHubActionsService, SettingsHubPostSaveService, SettingsHubStateService],
    templateUrl: './settings-hub.component.html',
    styleUrl: './settings-hub.component.scss'
})
export class SettingsHubComponent implements OnDestroy {
    protected readonly settingsState = inject(SettingsHubStateService);
    private readonly settingsActions = inject(SettingsHubActionsService);
    private readonly settingsPostSave = inject(SettingsHubPostSaveService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly state = inject($state);
    private readonly toolbarEventsService = inject(ToolbarEventsService);
    private readonly translateService = inject(TranslateService);

    protected readonly isMobile = mobileCheck();
    protected readonly showDetail = signal(!!(this.state.params['section'] || this.state.params['setting']));
    protected readonly searchQuery = signal('');
    protected readonly selectedSubcategoryId = signal<string | null>(this.getSubcategoryId(this.state.params['section'], this.state.params['setting']));
    /** Changes every time the page navigates to a subcategory, to expand its group in the sidebar */
    protected readonly navigationCount = signal(0);

    /** The setting to bring in view as soon as it is rendered: it may not be in the DOM yet (data still loading, section changing) */
    private readonly pendingSettingId = signal<string | null>(this.state.params['setting'] ?? null);
    private lastFocusedSettingId: string | null = null;
    private highlightTimeout?: ReturnType<typeof setTimeout>;
    private readonly keysBySubcategory = getPreferenceKeysBySubcategory(SETTINGS_REGISTRY);
    private searchIndex?: SettingsSearchEntry[];
    /** The search is waiting for the answer of the user about the payment methods that would be lost; the user may keep typing meanwhile */
    private askingAboutSearch = false;
    private latestSearch = '';
    /** Changes with the language, so that the results of the search that depend on the translations are computed again */
    private readonly languageVersion = signal(0);
    private readonly unsubscribeOnBefore: Function;
    private readonly unsubscribeOnSuccess: Function;

    protected readonly isSearching = computed(() => isSearchable(this.searchQuery()));
    protected readonly minSearchLength = MIN_SEARCH_LENGTH;
    /** Something is typed in the search, but not enough to search yet */
    protected readonly isSearchTooShort = computed(() => !this.isSearching() && this.searchQuery().trim().length > 0);

    protected readonly categories = computed(() => getVisibleCategories(SETTINGS_REGISTRY, this.settingsState.context()));

    /** The subcategories that contain settings changed and not saved yet: they are marked in the sidebar */
    protected readonly subcategoriesWithChanges = computed(() => {
        const changedKeys = new Set<string>(this.settingsState.changedKeys());

        return new Set([...this.keysBySubcategory].filter(([, keys]) => [...keys].some(key => changedKeys.has(key))).map(([subcategoryId]) => subcategoryId));
    });

    protected readonly selectedSubcategory = computed(() => {
        const subcategories = this.categories().flatMap(category => category.subcategories);

        return subcategories.find(subcategory => subcategory.id === this.selectedSubcategoryId()) ?? subcategories[0];
    });

    /** The cards of the selected subcategory or, when the user is searching, the ones with the settings found */
    protected readonly cards = computed<SettingsViewCard[]>(() => {
        const context = this.settingsState.context();

        if (this.isSearching()) {
            return buildSearchCards(this.searchResults(), context);
        }

        return buildSubcategoryCards(this.selectedSubcategory(), context);
    });

    /** The settings found: they depend on the query and the language only, not on what the user edits */
    private readonly searchResults = computed(() => this.isSearching() ? searchSettings(this.getSearchIndex(), this.searchQuery()) : []);

    /** The path of each setting found, for its tooltip and its aria-label */
    protected readonly settingPaths = computed(() => {
        this.languageVersion();
        const translate = (key: string, params?: Record<string, string | number>) => this.translateService.instant(key, params);

        return new Map(this.searchResults().map(({ category, subcategory, card, setting }) =>
            [setting.id, getSettingPath({ subcategoryId: subcategory.id, subcategoryTitle: subcategory.title, categoryTitle: category.title }, card, setting, translate)] as const));
    });

    /** The sections (subcategories) that the search names by their title, or by the one of their category: they are reached with a button */
    protected readonly sectionResults = computed(() => {
        if (!this.isSearching()) {
            return [];
        }

        this.languageVersion();
        const context = this.settingsState.context();

        return searchSections(this.categories(), this.searchQuery(), key => this.translateService.instant(key)).map(({ category, subcategory }) => ({
            id: subcategory.id,
            icon: subcategory.icon,
            title: subcategory.title,
            categoryTitle: category.title,
            settingsCount: buildSubcategoryCards(subcategory, context).reduce((count, card) => count + card.settings.length, 0)
        }));
    });

    protected readonly searchSummaryParams = computed(() => ({
        settings: this.cards().reduce((count, card) => count + card.settings.length, 0),
        sections: this.cards().length
    }));

    constructor() {
        this.toolbarEventsService.moduleTitle.next('SETTINGS_HUB');
        this.toolbarEventsService.searchBarValue$.next('');
        this.toolbarEventsService.searchBarIcon$.next('');

        subscribeInComponent(this.toolbarEventsService.events, event => {
            switch (event.type) {
                case 'toolbar.search':
                    this.onSearch(event.search);
                    break;
                case 'toolbar.clear_search_bar':
                    this.onSearch('');
                    break;
            }
        });

        subscribeInComponent(this.translateService.onLangChange, () => {
            this.searchIndex = undefined;
            this.languageVersion.update(version => version + 1);
        });

        // The user is not leaving the page, so the changes of the settings are kept and nothing is asked about them, when the transition:
        // - changes only the section: it is dynamic, the page is not reloaded;
        // - changes nothing (e.g. the section already open is chosen again): it is ignored by the router, but only after this hook
        // The payment methods are the exception: they are saved on their own and are lost when their section is closed.
        this.unsubscribeOnBefore = this.state.router.transitionService.onBefore(
            { from: HUB_STATE },
            (transition: Transition) => transition.ignored() || (transition.dynamic() ? this.confirmLeavingSection(this.getSubcategoryId(transition.params().section, transition.params().setting)) : this.confirmDiscardChanges())
        );

        // Follows the section and the setting of the url when they change from outside (e.g. browser back button)
        this.unsubscribeOnSuccess = this.state.router.transitionService.onSuccess(
            { to: HUB_STATE },
            (transition: Transition) => {
                const { section, setting } = transition.params();
                const subcategoryId = this.getSubcategoryId(section, setting);

                // A change that does not come from the page (the browser back button): the section replaces the results of a search,
                // and its group is opened in the menu, as when the user chooses it
                if (this.isSearching() || subcategoryId !== this.selectedSubcategoryId()) {
                    this.latestSearch = '';
                    this.searchQuery.set('');
                    this.toolbarEventsService.searchBarValue$.next('');
                    this.navigationCount.update(count => count + 1);
                }

                this.selectedSubcategoryId.set(subcategoryId);

                if (!setting) {
                    this.lastFocusedSettingId = null;
                }

                if (setting && setting !== this.lastFocusedSettingId) {
                    this.pendingSettingId.set(setting);
                }
            }
        );

        // Opening another section shows it from the top, not from the point where the previous one was scrolled to.
        // When the section is opened to reach a setting, that setting is brought in view instead.
        let shownSubcategoryId = this.selectedSubcategory().id;

        effect(() => {
            const subcategoryId = this.selectedSubcategory().id;

            if (subcategoryId !== shownSubcategoryId) {
                shownSubcategoryId = subcategoryId;

                if (!untracked(() => this.pendingSettingId())) {
                    this.scrollToTop();
                }
            }
        });

        // Brings the setting in view when the cards that contain it are rendered
        effect(() => {
            const settingId = this.pendingSettingId();

            if (settingId && this.settingsState.ready() && this.cards().some(card => card.settings.some(setting => setting.id === settingId))) {
                this.pendingSettingId.set(null);
                setTimeout(() => this.focusSetting(settingId));
            }
        }, { allowSignalWrites: true });

        this.settingsState.init();
    }

    ngOnDestroy() {
        this.unsubscribeOnBefore();
        this.unsubscribeOnSuccess();
        clearTimeout(this.highlightTimeout);
    }

    /**
     * Whatever the search changes on screen (it starts, it is refined, it is cleared) would lose the payment methods being
     * edited, whether they are in their section or among the results: the user is asked, and if refuses the search stays as it was.
     */
    private onSearch(search: string) {
        this.latestSearch = search;

        if (this.askingAboutSearch) {
            return;
        }

        const changesWhatIsShown = this.isSearching() || isSearchable(search);

        if (!changesWhatIsShown || search === this.searchQuery() || !this.settingsState.hasPaymentMethodChanges()) {
            this.applySearch(search);
            return;
        }

        this.askingAboutSearch = true;

        void this.confirmLosingPaymentMethodChanges().then(confirmed => {
            this.askingAboutSearch = false;

            if (confirmed) {
                this.applySearch(this.latestSearch);
            } else {
                this.latestSearch = this.searchQuery();
                this.toolbarEventsService.searchBarValue$.next(this.searchQuery());
            }
        });
    }

    private applySearch(search: string) {
        this.searchQuery.set(search);

        if (isSearchable(search)) {
            this.showDetail.set(true);
        }
    }

    protected onSubcategorySelect(subcategoryId: string) {
        // The section is already open: there is nothing to navigate to
        if (!this.isSearching() && subcategoryId === this.selectedSubcategory().id) {
            this.showDetail.set(true);
            return;
        }

        this.navigateTo(subcategoryId);
    }

    /** Opens the subcategory of a result of the search, with the setting in view and highlighted */
    protected goToSetting(card: SettingsViewCard, setting: SettingDefinition) {
        this.navigateTo(card.origin!.subcategoryId, setting.id);
    }

    /** The value shown by a setting: the one being edited, or for a setting of the device the one kept by the device */
    protected valueOf(setting: SettingDefinition): SettingValue {
        return setting.control === 'device-toggle' ? this.settingsActions.deviceSetting(setting.deviceSettingId) : this.settingsState.getValue(setting);
    }

    protected onValueChange(setting: SettingDefinition, value: SettingValue) {
        if (setting.control === 'device-toggle') {
            // A setting of the device is changed at once: it is not one of the changes that the page saves
            void this.setDeviceSetting(setting.deviceSettingId, value === true);
        } else if (isPreferenceSetting(setting)) {
            this.settingsState.setValue(setting, value);
        }
    }

    private async setDeviceSetting(deviceSettingId: SettingsDeviceSettingId, enabled: boolean) {
        const result = await this.settingsActions.setDeviceSetting(deviceSettingId, enabled);

        if (result) {
            this.settingsState.applyValues(result.values, result.persisted);
        }
    }

    protected async save() {
        // The running save gives its keys to its own call: a refused one must not take them
        if (this.settingsState.isSaving) {
            return;
        }

        await this.settingsState.save();

        // What follows a save depends on what was written: also when the save stopped halfway, the preferences already written count
        const savedKeys = this.settingsState.takeSavedKeys();

        if (savedKeys.length) {
            await this.settingsPostSave.handle(savedKeys);
        }
    }

    protected readonly lookup = (lookupId: SettingsLookupId) => this.settingsActions.lookup(lookupId);

    protected async runAction(actionId: SettingsActionId) {
        const result = await this.settingsActions.run(actionId, this.settingsState.context());

        if (result) {
            this.settingsState.applyValues(result.values, result.persisted);
        }
    }

    /** Goes to a section, after asking the user if payment methods being edited would be lost: nothing changes until the answer */
    private navigateTo(subcategoryId: string, settingId: string | null = null) {
        if (!this.wouldLosePaymentMethodChanges(subcategoryId)) {
            this.goToSection(subcategoryId, settingId);
            return;
        }

        void this.confirmLeavingSection(subcategoryId).then(confirmed => confirmed && this.goToSection(subcategoryId, settingId));
    }

    private goToSection(subcategoryId: string, settingId: string | null) {
        this.lastFocusedSettingId = null;
        this.searchQuery.set('');
        this.toolbarEventsService.searchBarValue$.next('');
        this.selectedSubcategoryId.set(subcategoryId);
        this.showDetail.set(true);
        this.navigationCount.update(count => count + 1);
        this.pendingSettingId.set(settingId);
        this.state.go(HUB_STATE, { section: subcategoryId, setting: settingId });
    }

    /** The subcategory of the url: the one asked or, if only a setting is asked, the one that contains it */
    private getSubcategoryId(section: string | null | undefined, settingId: string | null | undefined) {
        return section ?? (settingId ? findSettingLocation(SETTINGS_REGISTRY, settingId)?.subcategory.id : undefined) ?? null;
    }

    /** The page scrolls inside the body of the main area, not the window */
    private scrollToTop() {
        const scroller = this.host.nativeElement.querySelector('[mainBody]')?.closest<HTMLElement>('.tw-overflow-y-auto');

        if (scroller) {
            scroller.scrollTop = 0;
        }
    }

    /** Scrolls the setting to the center of the page and highlights it for a moment */
    private focusSetting(settingId: string) {
        const element = document.getElementById(`setting-${settingId}`);

        if (!element) {
            return;
        }

        this.lastFocusedSettingId = settingId;

        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        clearTimeout(this.highlightTimeout);
        document.querySelectorAll('.settings-highlight').forEach(highlighted => highlighted.classList.remove('settings-highlight'));

        // Reading the offset width restarts the animation when the same setting is highlighted twice in a row
        void element.offsetWidth;
        element.classList.add('settings-highlight');

        this.highlightTimeout = setTimeout(() => element.classList.remove('settings-highlight'), HIGHLIGHT_DURATION);
    }

    /** The search index is built on first use and rebuilt when the language changes, because it contains the translated labels */
    private getSearchIndex() {
        this.languageVersion();
        this.searchIndex ??= buildSearchIndex(flattenSettings(SETTINGS_REGISTRY), key => this.translateService.instant(key));
        return this.searchIndex;
    }

    private wouldLosePaymentMethodChanges(targetSubcategoryId: string | null) {
        // While searching, what is on screen are the results, even if the section that was open before is the one chosen
        return this.settingsState.hasPaymentMethodChanges() && (this.isSearching() || targetSubcategoryId !== this.selectedSubcategory().id);
    }

    /**
     * Asks the user before going to another section with payment methods that have changes not saved.
     * It is asked when the user chooses the section, before the page changes, and also by the router for the changes that come from
     * outside (e.g. the back button of the browser): when the page already changed there is nothing to ask.
     */
    private async confirmLeavingSection(targetSubcategoryId: string | null) {
        return !this.wouldLosePaymentMethodChanges(targetSubcategoryId) || this.confirmLosingPaymentMethodChanges();
    }

    private async confirmLosingPaymentMethodChanges() {
        return !!(await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'SETTINGS.SETTINGS.WANT_TO_CONTINUE_PAYMENT',
                confirmLabel: 'APPLICATION.YES',
                cancelLabel: 'APPLICATION.NO',
                disableClose: true
            }
        }));
    }

    /** Asks the user before leaving the page with changes that are not saved */
    private async confirmDiscardChanges() {
        if (!this.settingsState.isDirty() && !this.settingsState.hasPaymentMethodChanges()) {
            return true;
        }

        return !!(await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'SETTINGS.HUB.UNSAVED_CHANGES_CONFIRM',
                confirmLabel: 'APPLICATION.YES',
                cancelLabel: 'APPLICATION.NO',
                disableClose: true
            }
        }));
    }
}
